import { Component, ElementRef, Inject, Injector, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AbstractComponent } from '../abstract.component';
import { MediaPictureService } from 'projects/common/services/media-picture.service';
import { PictureData } from '../picture/picture.component';
import { ButtonOptions } from '../eva-button/eva-button.component';
import { Module } from 'projects/common/model/ModuleModel';

enum FormStatus {
  INITIAL = 'INITIAL',
  MAIL_SENT = 'MAIL_SENT',
  ALREADY_SUBSCRIBED = 'ALREADY_SUBSCRIBED'
}

interface SubscriptionPayload {
  email: string;
  flow: string;
  origin: string;
  who: string;
  brand?: string;
  language?: string;
  country?: string;
}

interface SubscriptionFormModel {
  initial_picture: PictureData;
  initial_title: string;
  initial_description_text: string;
  mail_sent_picture: PictureData;
  mail_sent_title: string;
  mail_sent_description_text: string;
  already_subscribe_picture: PictureData;
  already_subscribe_title: string;
  already_subscribe_description_text: string;
  legal_link: ButtonOptions;
  see_offers_button: ButtonOptions;
}
interface SubscriptionFormModule extends Module<SubscriptionFormModel> {}

@Component({
  selector: 'subscription-form',
  template: `
<!--    INITIAL -->
    <div *ngIf="formStatus === 'INITIAL'" class="subscription-container-banner -eva-3-shadow-static">
      <div class="subscription-picture-banner -initial">
        <img class="picture" loading="lazy"
          [alt]="this.mediaPictureService.getAltByPictureData(module.data.initial_picture, this.screenType)"
          [src]="this.mediaPictureService.getMediaUrlByPictureData(module.data.initial_picture, this.screenType)">
      </div>
      <div class="subscription-banner-content">
        <div class="subscription-text-banner">
        <span class="subscription-form-title">
          {{ module.data.initial_title }}
        </span>
          <p class="subscription-form-subtitle eva-3-body-1">{{ module.data.initial_description_text }}</p>
          <p>
            <a class="eva-3-link" [href]="module.data.legal_link.link" target="_blank">{{module.data.legal_link.link_text}}</a>
          </p>
        </div>

        <div class="subscription-email-banner eva-3-input -md">
          <div #input class="input-container eva-3-validation -top-left">
            <span class="validation-msg">{{'subscription.form.initial.invalidAddress' | translate}}</span>
            <input [(ngModel)]="emailAddress" type="email" placeholder="{{'subscription.form.initial.placeholder' | translate}}" class="input-tag">
          </div>
        </div>
        <div class="subscription-button-banner">
          <span class="-md -primary eva-3-btn" (click)="subscribe()">
            <em class="btn-text">{{'subscription.form.initial.cta.text' | translate}}</em>
          </span>
        </div>
      </div>
    </div>

<!--    MAIL_SENT-->
    <div *ngIf="formStatus === 'MAIL_SENT'" class="subscription-container-banner -eva-3-shadow-static">
      <div class="subscription-picture-banner -sent">
        <img class="picture" loading="lazy"
          [alt]="this.mediaPictureService.getAltByPictureData(module.data.mail_sent_picture, this.screenType)"
          [src]="this.mediaPictureService.getMediaUrlByPictureData(module.data.mail_sent_picture, this.screenType)">
      </div>
      <div class="subscription-banner-content -sent">
        <div class="subscription-text-banner -sent">
          <span class="subscription-form-title">
            {{ module.data.mail_sent_title }}
          </span>
          <p class="eva-3-body-1">
            <span>
              {{ module.data.mail_sent_description_text }}
            </span>
            <span class="subscription-email-address -eva-3-mr-md">{{ emailAddress }}.</span>
            <span class="-md -primary eva-3-link" (click)="changeEmail()">
              <em class="btn-text">{{'subscription.form.sent.wrongMail' | translate}}</em>
            </span>
          </p>
        </div>
        <div class="subscription-button-banner">
          <a class="-md -primary eva-3-btn" [href]="module.data.see_offers_button.link" target="_blank">
            <em class="btn-text">{{module.data.see_offers_button.link_text}}</em>
          </a>
        </div>
      </div>
    </div>

<!--    ALREADY_SUBSCRIBED-->
    <div *ngIf="formStatus === 'ALREADY_SUBSCRIBED'" class="subscription-container-banner -eva-3-shadow-static">
      <div class="subscription-picture-banner -already-subscribed">
        <img class="picture" loading="lazy"
          [alt]="this.mediaPictureService.getAltByPictureData(module.data.already_subscribe_picture, this.screenType)"
          [src]="this.mediaPictureService.getMediaUrlByPictureData(module.data.already_subscribe_picture, this.screenType)">
      </div>
      <div class="subscription-banner-content -already-subscribed">
        <div class="subscription-text-banner -already-subscribed">
        <span class="subscription-form-title">
          {{ module.data.already_subscribe_title }}
        </span>
          <p class="subscription-form-subtitle eva-3-body-1">
            <span class="subscription-email-address">{{ emailAddress }}</span>
            {{ module.data.already_subscribe_description_text }}
          </p>
          <p>
            <a class="eva-3-link" [href]="module.data.legal_link.link" target="_blank">{{module.data.legal_link.link_text}}</a>
          </p>
        </div>

        <div class="subscription-button-banner">
          <span class="-md -primary eva-3-btn" (click)="reset()">
            <em class="btn-text">{{'subscription.form.alreadySubscribed.cta.text' | translate}}</em>
          </span>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./subscription-form.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class SubscriptionFormComponent extends AbstractComponent {

  getModuleId(): string {
    return "SubscriptionFormModule";
  }

  @Input() override module: SubscriptionFormModule;
  window: any;
  subscriptionBrand: string;
  country: string;
  language: string;

  formStatus = FormStatus.INITIAL;
  emailAddress: string;

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  constructor(injector: Injector, @Inject(DOCUMENT) private document: Document,
    public mediaPictureService: MediaPictureService) {
    super(injector);
    this.window = this.document.defaultView;
    this.subscriptionBrand = this.landingModelService.getLandingModel().partner?.group;
    this.country = this.landingModelService.getLandingModel().country;
    this.language = this.landingModelService.getLandingModel().language;
  }

  changeEmail(): void {
    this.formStatus = FormStatus.INITIAL;
  }

  subscribe(): void {
    if (this.isEmail()) {
      this.sendInfo();
      this.input.nativeElement.classList.remove('-invalid');
    } else {
      this.input.nativeElement.classList.add('-invalid');
    }
  }

  reset(): void {
    this.emailAddress = '';
    this.formStatus = FormStatus.INITIAL;
  }

  isEmail(): boolean {
    const regex = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
    return regex.test(this.emailAddress);
  }

  private getPayload() {
    const subscriptionPayload: SubscriptionPayload = {
      email: window.btoa(this.emailAddress) + '1',
      flow: 'landing',
      origin: 'landing',
      who: 'nymeria-gui'
    };
    if (this.subscriptionBrand) {
      subscriptionPayload.brand = this.subscriptionBrand;
      subscriptionPayload.country = this.country;
      subscriptionPayload.language = this.language;
    }
    return subscriptionPayload;
  }

  sendInfo(): void {
    const request = new XMLHttpRequest();
    request.open('POST', '/me/subscriptions-ui/api/subscriptions', true);
    request.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    request.onreadystatechange = () => {
      if (request.readyState === XMLHttpRequest.DONE && request.status === 200) {
        const response = JSON.parse(request.response).message;
        if (response === 'MAIL_SENDED') {
          this.formStatus = FormStatus.MAIL_SENT;
        }
        if (response === 'ALREADY_SUBSCRIBED') {
          this.formStatus = FormStatus.ALREADY_SUBSCRIBED;
        }
      }
    };

    request.onerror = (err) => {
      if (this.window.newrelic && this.window.newrelic.noticeError) {
        this.window.newrelic.noticeError(`Error trying to register mail to subscriptions API: ${err.toString()}`);
      }
    };

    request.send(JSON.stringify(this.getPayload()));
  }
}
